import { Archive, Server } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../../utils/cn';
import type { LogbookServerView } from '../../hooks/useLogbookServers';

interface LogbookServerChipProps {
  serverId: string;
  server?: LogbookServerView;
  /** Adds the pack thumbnail and the live player count. */
  detailed?: boolean;
  className?: string;
}

/**
 * A Logbook `server_id` rendered as the pack it actually is.
 *
 * Server ids are server addresses, so the pack list supplies the title, cluster and live state. An
 * id with no pack-list entry still renders as itself: a retired server keeps its history and is
 * expected to outlive its listing, so an unmatched id is normal rather than an error.
 */
export const LogbookServerChip = ({ serverId, server, detailed = false, className }: LogbookServerChipProps) => {
  const pack = server?.pack ?? null;
  const label = pack?.title ?? serverId;
  const title = pack
    ? `${pack.title}${pack.version ? ` ${pack.version}` : ''} on ${serverId}${server?.cluster ? ` (${server.cluster})` : ''}`
    : serverId;

  const body = (
    <>
      {detailed && pack?.thumbnailUrl ? (
        <img src={pack.thumbnailUrl} alt="" className="w-4 h-4 rounded-[3px] object-cover shrink-0" loading="lazy" />
      ) : (
        <Server className="w-3.5 h-3.5 shrink-0 text-glass-400" />
      )}
      <span className="truncate">{label}</span>
      {server?.archived && <Archive className="w-3 h-3 shrink-0 text-glass-500" aria-label="Archived server" />}
      {pack && (
        <span
          className={cn('w-1.5 h-1.5 rounded-full shrink-0', pack.online ? 'bg-green-400' : 'bg-glass-500')}
          aria-label={pack.online ? 'Online' : 'Offline'}
        />
      )}
      {detailed && pack?.online && (
        <span className="text-[11px] tabular-nums text-glass-500 shrink-0">{pack.players}/{pack.maxPlayers}</span>
      )}
    </>
  );

  const classes = cn('chip max-w-full min-w-0 gap-1.5', className);

  // The pack route is the useful destination when we know which pack this address runs.
  if (pack?.mpiId != null) {
    return <Link to={`/modpacks/${pack.mpiId}`} className={cn(classes, 'hover:border-glass-200/25')} title={title}>{body}</Link>;
  }
  return <span className={classes} title={title}>{body}</span>;
};
